/**
 * @fileoverview Acha as ocorrências do termo buscado no texto de uma mensagem
 * já desenhada na tela e devolve os trechos para o navegador grifar.
 *
 * A regra de o que é uma ocorrência vem do servidor: é a expressão regular que
 * a busca devolve (`pattern`), a mesma que o banco usou para contar. Aqui só
 * se aplica essa expressão ao texto como o usuário lê, juntando os pedaços que
 * a formatação separa (negrito, menção, link).
 */

/**
 * Texto de um elemento como o usuário lê e onde cada pedaço começa nele.
 * A quebra de linha (<br>) entra como "\n" para as linhas não se emendarem.
 */
export const collectText = (root) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  const nodes = [];
  let text = "";
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (node.nodeType === Node.TEXT_NODE) {
      if (node.data) {
        nodes.push({ node, start: text.length });
        text += node.data;
      }
    } else if (node.nodeName === "BR") {
      text += "\n";
    }
  }
  return { text, nodes };
};

/**
 * Onde cada ocorrência começa e termina, em nó de texto e posição dentro dele.
 * As ocorrências vêm da esquerda para a direita, sem sobreposição, até `max`.
 */
export const findOccurrences = (root, regex, max = Infinity) => {
  const { text, nodes } = collectText(root);
  const found = [];
  if (!nodes.length) return found;

  const matcher = regex;
  matcher.lastIndex = 0;
  let first = 0;
  for (let match = matcher.exec(text); match && found.length < max; match = matcher.exec(text)) {
    if (!match[0]) {
      // Não acontece com a expressão da busca; evita ficar parado no mesmo ponto.
      matcher.lastIndex += 1;
    } else {
      const start = match.index;
      const end = start + match[0].length;
      while (first + 1 < nodes.length && nodes[first + 1].start <= start) first += 1;
      let last = first;
      while (last + 1 < nodes.length && nodes[last + 1].start < end) last += 1;
      found.push({
        startNode: nodes[first].node,
        startOffset: Math.min(start - nodes[first].start, nodes[first].node.data.length),
        endNode: nodes[last].node,
        endOffset: Math.min(end - nodes[last].start, nodes[last].node.data.length),
      });
    }
  }
  return found;
};

/** As ocorrências como trechos (Range) do documento. */
export const buildRanges = (root, regex, max) =>
  findOccurrences(root, regex, max).map(({ startNode, startOffset, endNode, endOffset }) => {
    const range = document.createRange();
    range.setStart(startNode, startOffset);
    range.setEnd(endNode, endOffset);
    return range;
  });
